import { setupClass } from '../Base.js'
import { gApp } from '../Globals.js'
import { reactive } from 'vue'

/**
 * Class to manage admin-related data and functionality
 */
export class AdminData {
  init() {
    this.ADMIN_PASSWORD_KEY = 'heatwise-admin-password';
    this.adminPassword = null; // Stores the last reloaded admin password
    this.userData = null; // Stores the user data from admin endpoint
    this.filterText = ''; // Stores the current filter text for users
    this.filterByActiveTrials = false; // Filter to show only active free trials
    this.tableSorting = { // Stores the sorting state for the user table
      column: null,
      direction: 'asc'
    };
  }

  /**
   * Reload the admin password from localStorage and store it in the adminPassword field
   * @returns {string|null} The reloaded admin password or null if not found
   */
  reloadAdminPassword() {
    try {
      this.adminPassword = localStorage.getItem(this.ADMIN_PASSWORD_KEY);
      return this.adminPassword;
    } catch (error) {
      console.error('Error reloading admin password from localStorage:', error);
      this.adminPassword = null;
      return null;
    }
  }

  /**
   * Set the admin password in localStorage
   * @param {string} password - The admin password to store
   * @returns {boolean} True if successful, false otherwise
   */
  setAdminPassword(password) {
    try {
      this.adminPassword = password;
      localStorage.setItem(this.ADMIN_PASSWORD_KEY, password);
      return true;
    } catch (error) {
      console.error('Error setting admin password in localStorage:', error);
      return false;
    }
  }

  /**
   * Reload user data from the admin/get-all-users endpoint
   * @returns {Promise<Object|null>} The user data response or null if failed
   */
  async reloadUserData() {
    try {
      if (!this.adminPassword) {
        console.error('No admin password available for reloadUserData');
        return null;
      }
      const response = await gApp.fetchFromServer('admin/get-all-users', 'POST', {
        adminPassword: this.adminPassword
      });

      console.log('Successfully reloaded user data from admin endpoint');
      this.userData = response; // Store the user data
      return response;
    } catch (error) {
      console.error('Error reloading user data:', error);
      this.userData = null; // Clear user data on error
      return null;
    }
  }

  /**
   * Get filtered users based on the stored filter text and active trials filter
   * @returns {Array} Array of filtered users or all users if no filter
   */
  getFilteredUsers() {
    if (!this.userData) {
      return []
    }
    
    let users = this.userData.users
    
    // Filter by active trials if enabled
    if (this.filterByActiveTrials) {
      users = users.filter(user => {
        if (user.trialType !== 'FreeTrial') return false
        
        // Exclude indefinite trials
        if (user.trialExpiry === 'indefinite') return false
        
        if (user.trialExpiry && user.trialExpiry !== '-') {
          const expiryDate = new Date(user.trialExpiry)
          const now = new Date()
          return expiryDate > now
        }
        
        return false
      })
    }
    
    // Filter by email text if provided
    if (this.filterText.trim()) {
      const filter = this.filterText.toLowerCase().trim()
      users = users.filter(user => 
        user.email.toLowerCase().includes(filter)
      )
    }
    
    return users
  }

  /**
   * Get user data formatted for InteractiveTable component
   * @returns {Object} Reactive table data object with columns and rowData properties
   */
  getUserDataAsTableData() {
    const users = this.getFilteredUsers()
    
    const columns = [
      { key: 'email', label: 'Email', type: 'String' },
      { key: 'creationTime', label: 'Creation Time', type: 'Date' },
      { key: 'trialType', label: 'Trial Type', type: 'Enum' },
      { key: 'trialExpiry', label: 'Trial Expiry', type: 'Date' },
      { key: 'projectCount', label: '# Projects', type: 'Number' },
      { key: 'lastProjectDate', label: 'Last Project Date', type: 'Date' }
    ]
    
    const rowData = users.map(user => ({
      email: user.email,
      creationTime: user.creationTime || null,
      trialType: user.trialType || 'None',
      trialExpiry: user.trialExpiry === 'indefinite' ? 'Indefinite' : user.trialExpiry || null,
      projectCount: user.projectCount,
      lastProjectDate: user.lastProjectDate || null
    }))
    
    return reactive({
      columns,
      rowData,
      sorting: this.tableSorting,
      highlightColor: (row) => {
        // Highlight active free trials in green
        if (row.trialType === 'FreeTrial') {
          // Check if trial is active (indefinite or not expired)
          if (row.trialExpiry === 'Indefinite') {
            return '#d1ecf1' // Light blue for indefinite trials
          } else if (row.trialExpiry && row.trialExpiry !== '-') {
            // Check if trial hasn't expired yet
            const expiryDate = new Date(row.trialExpiry)
            const now = new Date()
            if (expiryDate > now) {
              // Check if trial expires within 7 days
              const sevenDaysFromNow = new Date()
              sevenDaysFromNow.setDate(now.getDate() + 7)
              
              if (expiryDate <= sevenDaysFromNow) {
                return '#ffdb4d' // Lighter yellow/orange for trials expiring soon
              } else {
                return '#5cb85c' // Lighter green for active trials
              }
            }
          }
        }
        return null // No highlight for other rows
      }
    })
  }

}
setupClass(AdminData)
